import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GitCompare, RefreshCw, Loader2, CheckCircle2, AlertTriangle, Play, History, Layers, Eye, ChevronDown, ChevronUp, Mail } from 'lucide-react';
import { API } from '../lib/api';

const token = () => sessionStorage.getItem('erp_security_token') || localStorage.getItem('erp_security_token') || '';
const hdr = () => ({ Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' });
const api = async (path, opts = {}) => {
  const r = await fetch(`${API}/schema-sync${path}`, { ...opts, headers: hdr() });
  const d = await r.json();
  if (!r.ok || d.ok === false) throw new Error(d.error || `HTTP ${r.status}`);
  return d;
};

const C = { panel: 'rgba(15,23,42,0.8)', border: 'rgba(148,163,184,0.15)', text: '#f1f5f9', muted: '#64748b', ok: '#10b981', warn: '#fbbf24', err: '#f87171', brand: '#3EC46A' };
const btn = (bg, fg) => ({ padding: '7px 14px', borderRadius: 7, background: bg, border: `1px solid ${fg}55`, color: fg, cursor: 'pointer', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6 });

export default function SchemaSyncPage() {
  const [summary, setSummary]   = useState(null);
  const [canonical, setCanonical] = useState('RC26ERP_DEV');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [selected, setSelected] = useState(null);
  const [report, setReport]     = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [opts, setOpts]         = useState({ addColumns: true, widen: true, addTables: false, rebuildDeps: true });
  const [drift, setDrift]       = useState(null);
  const [driftRunning, setDriftRunning] = useState(false);
  const [jobs, setJobs]         = useState([]);
  const [history, setHistory]   = useState(null);
  const [migStatus, setMigStatus] = useState(null);
  const [migRunning, setMigRunning] = useState(false);
  const pollRef = useRef(null);

  const loadSummary = useCallback(async () => {
    setLoading(true); setError('');
    try { const d = await api(`/report-all?canonical=${encodeURIComponent(canonical)}`); setSummary(d.databases); }
    catch (e) { setError(e.message); }
    setLoading(false);
  }, [canonical]);

  const loadJobs = useCallback(async () => {
    try { const d = await api('/jobs'); setJobs(d.jobs || []); } catch { /* ignore */ }
  }, []);
  const loadDrift = useCallback(async () => {
    try { setDrift(await api('/drift-alert/status')); } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadSummary(); loadJobs(); loadDrift(); }, [loadSummary, loadJobs, loadDrift]);

  const runDriftNow = async (force) => {
    if (force && !window.confirm('Send the drift alert email to the licensor mailbox now (even if nothing drifted)?')) return;
    setDriftRunning(true);
    try {
      await api('/drift-alert/run-now', { method: 'POST', body: JSON.stringify({ force }) });
      for (let i = 0; i < 60; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const d = await api('/drift-alert/status'); setDrift(d);
        if (!d.running) break;
      }
    } catch (e) { setError(e.message); }
    setDriftRunning(false);
  };

  useEffect(() => {
    const anyRunning = jobs.some(j => j.status === 'RUNNING');
    clearInterval(pollRef.current);
    if (anyRunning) pollRef.current = setInterval(loadJobs, 3000);
    return () => clearInterval(pollRef.current);
  }, [jobs, loadJobs]);

  const openReport = async (db) => {
    setSelected(db); setReport(null); setReportLoading(true); setHistory(null);
    try { const d = await api(`/report?target=${encodeURIComponent(db)}&canonical=${encodeURIComponent(canonical)}`); setReport(d.report); }
    catch (e) { setError(e.message); }
    setReportLoading(false);
  };

  const apply = async () => {
    if (!selected) return;
    const parts = [opts.addTables && `${report.summary.missingTables} tables`, opts.addColumns && `${report.summary.missingColumns} columns`, opts.widen && `${report.summary.widen} widenings`].filter(Boolean);
    if (!window.confirm(`Apply additive schema sync to ${selected}?\n\nWill add: ${parts.join(', ') || 'nothing'}.\nNothing is ever dropped or narrowed.`)) return;
    try { await api('/apply', { method: 'POST', body: JSON.stringify({ target: selected, canonical, ...opts }) }); await loadJobs(); }
    catch (e) { setError(e.message); }
  };

  const loadHistory = async () => {
    try { const d = await api(`/history?target=${encodeURIComponent(selected)}`); setHistory(d.rows); } catch (e) { setError(e.message); }
  };

  const runMigrations = async (db) => {
    if (!window.confirm(`Run versioned migrations on ${db || 'ALL ERP databases'}?`)) return;
    setMigRunning(true);
    try { const d = await api(`/migrations/run${db ? `?db=${encodeURIComponent(db)}` : ''}`, { method: 'POST' }); setMigStatus({ log: d.log, totalApplied: d.totalApplied, totalErrors: d.totalErrors }); await loadSummary(); }
    catch (e) { setError(e.message); }
    setMigRunning(false);
  };

  return (
    <div style={{ padding: 24, maxWidth: 1240, margin: '0 auto' }} data-testid="schema-sync-page">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <GitCompare size={22} color={C.brand} />
        <div>
          <h1 style={{ color: C.text, fontSize: 20, margin: 0, fontWeight: 800 }}>Schema Sync</h1>
          <p style={{ color: C.muted, fontSize: 11, margin: '3px 0 0' }}>Drift report vs canonical <code style={{ color: C.brand }}>{canonical}</code> · additive auto-fix only (add tables / add columns / widen). Drops & narrowing are report-only.</p>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <input value={canonical} onChange={e => setCanonical(e.target.value)} data-testid="schema-canonical-input" style={{ padding: '6px 10px', borderRadius: 7, background: 'rgba(15,23,42,0.6)', border: `1px solid ${C.border}`, color: C.text, fontSize: 11, width: 150 }} />
          <button onClick={loadSummary} disabled={loading} data-testid="schema-refresh-btn" style={btn('rgba(62,196,106,0.12)', C.brand)}><RefreshCw size={12} className={loading ? 'spin' : ''} /> Scan all</button>
          <button onClick={() => runMigrations()} disabled={migRunning} data-testid="schema-run-migrations-all" style={btn('rgba(59,130,246,0.15)', '#60a5fa')}>{migRunning ? <Loader2 size={12} className="spin" /> : <Layers size={12} />} Run migrations (all)</button>
        </div>
      </div>

      {error && <div data-testid="schema-error" style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 16, background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.4)', color: '#fecaca', fontSize: 12 }}><AlertTriangle size={13} /> {error}</div>}

      {drift && (
        <div data-testid="schema-drift-alert" style={{ marginBottom: 20, padding: '10px 14px', borderRadius: 8, background: C.panel, border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 12, fontSize: 11, color: '#cbd5e1', flexWrap: 'wrap' }}>
          <Mail size={13} color={C.warn} />
          <strong style={{ color: C.text }}>Nightly drift alert</strong>
          <span style={{ color: C.muted }}>{drift.cron ? `cron ${drift.cron} IST` : 'disabled (SCHEMA_DRIFT_ALERT_CRON not set)'} → {drift.recipient || 'no recipient'}</span>
          {drift.running && <span style={{ color: C.warn }}>· checking…</span>}
          {drift.lastRun && <span>· last run {new Date(drift.lastRun).toLocaleString()}: {drift.lastResult ? `${drift.lastResult.drifted.length} template(s) drifted, email ${drift.lastResult.emailed ? 'sent' : 'not sent'}${drift.lastResult.emailError ? ` (${drift.lastResult.emailError})` : ''}` : drift.lastError}</span>}
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <button onClick={() => runDriftNow(false)} disabled={driftRunning} data-testid="drift-check-now" style={btn('transparent', '#94a3b8')}>{driftRunning ? <Loader2 size={11} className="spin" /> : <RefreshCw size={11} />} Check now</button>
            <button onClick={() => runDriftNow(true)} disabled={driftRunning} data-testid="drift-send-test" style={btn('rgba(251,191,36,0.12)', C.warn)}><Mail size={11} /> Send test email</button>
          </span>
        </div>
      )}

      {jobs.length > 0 && (
        <div style={{ marginBottom: 20, display: 'grid', gap: 8 }} data-testid="schema-jobs">
          {jobs.slice().reverse().slice(0, 6).map(j => (
            <div key={j.jobId} data-testid={`schema-job-${j.target}`} style={{ padding: '8px 14px', borderRadius: 8, background: C.panel, border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 12, fontSize: 11, color: '#cbd5e1' }}>
              {j.status === 'RUNNING' ? <Loader2 size={13} className="spin" color={C.warn} /> : j.status === 'DONE' ? <CheckCircle2 size={13} color={C.ok} /> : <AlertTriangle size={13} color={C.err} />}
              <strong style={{ color: C.text }}>{j.target}</strong>
              <span style={{ color: C.muted }}>{j.status}</span>
              {j.progress?.total != null && <span>{j.progress.done}/{j.progress.total} · applied {j.progress.applied} · failed <span style={{ color: j.progress.failed ? C.err : C.muted }}>{j.progress.failed}</span></span>}
              {j.progress?.current && j.status === 'RUNNING' && <code style={{ color: C.muted, marginLeft: 'auto' }}>{j.progress.current}</code>}
              {j.error && <span style={{ color: C.err }}>{j.error}</span>}
            </div>
          ))}
        </div>
      )}

      {loading && !summary && <div style={{ textAlign: 'center', padding: 60, color: C.muted, fontSize: 13 }}><Loader2 size={20} className="spin" /><br />Introspecting all databases… (30–60s)</div>}

      {summary && (
        <div style={{ overflowX: 'auto', borderRadius: 10, border: `1px solid ${C.border}`, background: C.panel, marginBottom: 24 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }} data-testid="schema-summary-table">
            <thead><tr style={{ color: C.muted, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {['Database', 'Tables', 'Missing tables', 'Missing columns', 'Widen', 'Report-only', 'Extra', 'Status', ''].map(h => <th key={h} style={{ textAlign: 'left', padding: '10px 12px', borderBottom: `1px solid ${C.border}` }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {summary.map(d => (
                <tr key={d.target} data-testid={`schema-row-${d.target}`} style={{ borderBottom: `1px solid ${C.border}`, background: selected === d.target ? 'rgba(62,196,106,0.06)' : 'transparent' }}>
                  <td style={{ padding: '9px 12px', color: C.text, fontWeight: 600 }}>{d.target} {d.isTemplate && <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 999, background: 'rgba(59,130,246,0.15)', color: '#60a5fa', fontSize: 9, fontWeight: 800 }}>TEMPLATE</span>}</td>
                  {d.error ? <td colSpan={7} style={{ color: C.err, padding: '9px 12px' }}>{d.error}</td> : <>
                    <td style={{ padding: '9px 12px', color: '#cbd5e1' }}>{d.counts?.targetTables} / {d.counts?.canonicalTables}</td>
                    <Num v={d.missingTables} /><Num v={d.missingColumns} /><Num v={d.widen} /><Num v={d.reportOnly} neutral /><Num v={d.extraTables} neutral />
                    <td style={{ padding: '9px 12px' }}>{d.inSync ? <span style={{ color: C.ok, fontWeight: 700 }}>IN SYNC</span> : <span style={{ color: C.warn, fontWeight: 700 }}>DRIFT</span>}</td>
                  </>}
                  <td style={{ padding: '9px 12px', textAlign: 'right' }}><button onClick={() => openReport(d.target)} data-testid={`schema-view-${d.target}`} style={btn('transparent', '#94a3b8')}><Eye size={11} /> Details</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div style={{ borderRadius: 10, border: `1px solid ${C.border}`, background: C.panel, padding: 18 }} data-testid="schema-report-panel">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
            <h2 style={{ color: C.text, fontSize: 15, margin: 0 }}>{selected} <span style={{ color: C.muted, fontSize: 11, fontWeight: 400 }}>vs {canonical}</span></h2>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {[['addTables', 'Add missing tables'], ['addColumns', 'Add missing columns'], ['widen', 'Widen lengths'], ['rebuildDeps', 'Rebuild blocking indexes/constraints']].map(([k, l]) => (
                <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#cbd5e1', fontSize: 11 }}>
                  <input type="checkbox" checked={opts[k]} onChange={e => setOpts(o => ({ ...o, [k]: e.target.checked }))} data-testid={`schema-opt-${k}`} /> {l}
                </label>
              ))}
              <button onClick={apply} disabled={!report || report.summary.inSync} data-testid="schema-apply-btn" style={btn('rgba(62,196,106,0.15)', C.brand)}><Play size={11} /> Apply additive sync</button>
              <button onClick={() => runMigrations(selected)} disabled={migRunning} data-testid="schema-run-migrations-one" style={btn('rgba(59,130,246,0.15)', '#60a5fa')}><Layers size={11} /> Run migrations</button>
              <button onClick={loadHistory} data-testid="schema-history-btn" style={btn('transparent', '#94a3b8')}><History size={11} /> History</button>
            </div>
          </div>
          {reportLoading && <div style={{ color: C.muted, fontSize: 12 }}><Loader2 size={14} className="spin" /> Comparing…</div>}
          {report && (
            <div style={{ display: 'grid', gap: 12 }}>
              <Section title={`Missing tables (${report.missingTables.length}) — created only if "Add missing tables" is ticked`} testid="schema-sec-tables" rows={report.missingTables.map(t => [t.table, `${t.columns} cols`, t.sql])} />
              <Section title={`Missing columns (${report.missingColumns.length}) — added as NULL`} testid="schema-sec-columns" rows={report.missingColumns.map(c => [`${c.table}.${c.column}`, c.type, c.sql])} />
              <Section title={`Widen (${report.widen.length}) — safe length/precision increase (blocking indexes/constraints rebuilt automatically when ticked)`} testid="schema-sec-widen" rows={report.widen.map(w => [`${w.table}.${w.column}`, `${w.from} → ${w.to}`, w.sql])} />
              <Section title={`Report-only (${report.reportOnly.length}) — needs manual decision, never auto-applied`} testid="schema-sec-reportonly" rows={report.reportOnly.map(r => [`${r.table}.${r.column}`, r.kind, `${r.from} → ${r.to}`])} muted />
              <Section title={`Extra tables in target (${report.extraTables.length}) — never dropped`} testid="schema-sec-extra" rows={report.extraTables.map(t => [t, '', ''])} muted />
            </div>
          )}
          {history && (
            <div style={{ marginTop: 16 }} data-testid="schema-history">
              <h3 style={{ color: C.text, fontSize: 12, margin: '0 0 8px' }}>Sync history ({history.length})</h3>
              <div style={{ maxHeight: 260, overflow: 'auto', fontSize: 11 }}>
                {history.length === 0 && <div style={{ color: C.muted }}>No sync runs recorded for this database yet.</div>}
                {history.map(h => <div key={h.Id} style={{ display: 'flex', gap: 10, padding: '4px 0', borderBottom: `1px dashed ${C.border}`, color: '#cbd5e1' }}><span style={{ color: h.Status === 'OK' ? C.ok : C.err, fontWeight: 700, width: 34 }}>{h.Status}</span><span style={{ width: 90, color: C.muted }}>{h.Action}</span><span style={{ flex: 1 }}>{h.ObjectName}</span><span style={{ color: C.muted }}>{new Date(h.RunAt).toLocaleString()}</span>{h.Error && <span style={{ color: C.err }}>{h.Error}</span>}</div>)}
              </div>
            </div>
          )}
        </div>
      )}

      {migStatus && (
        <div style={{ marginTop: 20, borderRadius: 10, border: `1px solid ${C.border}`, background: C.panel, padding: 14 }} data-testid="schema-migration-log">
          <div style={{ color: C.text, fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Migration run — {migStatus.totalApplied} applied, {migStatus.totalErrors} errors</div>
          <pre style={{ margin: 0, fontSize: 10.5, color: '#cbd5e1', whiteSpace: 'pre-wrap', maxHeight: 260, overflow: 'auto' }}>{(migStatus.log || []).join('\n')}</pre>
        </div>
      )}
    </div>
  );
}

function Num({ v, neutral }) {
  const color = v === 0 ? C.muted : neutral ? '#cbd5e1' : C.warn;
  return <td style={{ padding: '9px 12px', color, fontWeight: v ? 700 : 400 }}>{v}</td>;
}

function Section({ title, rows, testid, muted }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={testid} style={{ border: `1px solid ${C.border}`, borderRadius: 8 }}>
      <button onClick={() => setOpen(o => !o)} style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', color: muted ? '#94a3b8' : C.text, fontSize: 12, fontWeight: 600, padding: '9px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
        {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />} {title}
      </button>
      {open && (
        <div style={{ maxHeight: 320, overflow: 'auto', padding: '0 12px 10px', fontSize: 11 }}>
          {rows.length === 0 && <div style={{ color: C.muted }}>None</div>}
          {rows.map(([a, b, c], i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '260px 180px 1fr', gap: 10, padding: '4px 0', borderBottom: `1px dashed ${C.border}`, color: '#cbd5e1' }}>
              <span style={{ fontWeight: 600 }}>{a}</span><span style={{ color: C.muted }}>{b}</span><code style={{ color: '#94a3b8', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{c}</code>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
